import { useState, useCallback, createContext, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SignUpPromptModal from '../components/SignUpPromptModal';

const AuthPromptContext = createContext(null);

/**
 * Provider that wraps the app and exposes requireAuth(action, callback).
 * Place high in the tree (inside AuthProvider).
 */
export const AuthPromptProvider = ({ children }) => {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(null);

  /**
   * requireAuth(action, callback, options)
   *
   *   action   — string key: 'book' | 'favorite' | 'checkout' | 'chat' | 'share' | 'profile'
   *   callback — function to run if signed in
   *   options  — { title, message, ctaLabel } optional overrides
   */
  const requireAuth = useCallback((action, callback, options = {}) => {
    if (user) {
      if (typeof callback === 'function') callback();
      return true;
    }
    const copy = getCopyForAction(action, options);
    setPrompt({ ...copy });
    return false;
  }, [user]);

  const close = () => setPrompt(null);

  return (
    <AuthPromptContext.Provider value={{ requireAuth }}>
      {children}
      <SignUpPromptModal
        open={!!prompt}
        onClose={close}
        title={prompt?.title}
        message={prompt?.message}
        ctaLabel={prompt?.ctaLabel}
        icon={prompt?.icon}
      />
    </AuthPromptContext.Provider>
  );
};

export const useAuthPrompt = () => {
  const ctx = useContext(AuthPromptContext);
  if (!ctx) {
    // Fallback so components don't crash if Provider missing
    return {
      requireAuth: (action, callback) => {
        if (typeof callback === 'function') callback();
        return true;
      },
    };
  }
  return ctx;
};

// ============================================================
// COPY FOR EACH ACTION
// ============================================================
const getCopyForAction = (action, overrides) => {
  const defaults = {
    book: {
      title: 'Sign up to book',
      message: 'Create a free Skytone account to book this service and manage your appointments.',
      ctaLabel: 'Create Account',
      icon: 'book',
    },
    favorite: {
      title: 'Sign up to save favorites',
      message: 'Create a free Skytone account to save your favorite products and services.',
      ctaLabel: 'Create Account',
      icon: 'favorite',
    },
    checkout: {
      title: 'Sign up to check out',
      message: 'Create a free Skytone account to complete your order.',
      ctaLabel: 'Create Account',
      icon: 'checkout',
    },
    chat: {
      title: 'Sign up to message',
      message: 'Create a free Skytone account to chat with professionals.',
      ctaLabel: 'Create Account',
      icon: 'chat',
    },
    share: {
      title: 'Sign up to share',
      message: 'Create a free Skytone account to share your cart and routines.',
      ctaLabel: 'Create Account',
      icon: 'share',
    },
    profile: {
      title: 'Sign up to continue',
      message: 'Create a free Skytone account to view your profile, orders, and calendar.',
      ctaLabel: 'Create Account',
      icon: 'profile',
    },
    generic: {
      title: 'Sign up to continue',
      message: 'Create a free Skytone account to unlock this feature.',
      ctaLabel: 'Create Account',
      icon: 'generic',
    },
  };
  return { ...(defaults[action] || defaults.generic), ...overrides };
};